import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? `Welcome back, ${user.name.split(" ")[0]}` : "Account created — welcome to RMS Group");
      if (user.role === "admin") return navigate("/admin");
      const next = params.get("next");
      navigate(next || "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-7xl items-center px-5 py-16 lg:px-8" data-testid="auth-page">
      <div className="grid w-full items-center gap-14 lg:grid-cols-2">
        <div className="hidden lg:block">
          <span className="overline-label">Client Portal</span>
          <h1 className="mt-5 font-serif text-5xl font-semibold leading-tight text-slate-50" data-testid="auth-headline">
            <MaskedLine delay={0.1}>Track your order</MaskedLine>
            <MaskedLine delay={0.3} className="italic text-[#C5A059]">every step of the way.</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
              Sign in to complete your career questionnaire, upload your existing CV, and follow your documents from HR review to final delivery.
            </p>
            <div className="mt-10">
              <Logo />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="card-surface mx-auto w-full max-w-md p-8 lg:p-10">
            <div className="mb-8 flex rounded-full border border-slate-700 p-1" data-testid="auth-mode-toggle">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(""); }}
                  data-testid={`auth-mode-${m}`}
                  className={`flex-1 rounded-full py-2.5 text-sm font-semibold capitalize transition-colors duration-200 ${mode === m ? "bg-[#C5A059] text-[#0A0F1D]" : "text-slate-400 hover:text-slate-200"}`}
                >
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
              {mode === "register" && (
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="auth-name">Full Name</label>
                  <input id="auth-name" data-testid="auth-name-input" required value={form.name} onChange={set("name")} className="input-dark" placeholder="Your full name" />
                </div>
              )}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="auth-email">Email</label>
                <input id="auth-email" data-testid="client-login-email-input" required type="email" value={form.email} onChange={set("email")} className="input-dark" placeholder="you@email.com" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="auth-password">Password</label>
                <input id="auth-password" data-testid="client-login-password-input" required type="password" minLength={6} value={form.password} onChange={set("password")} className="input-dark" placeholder="••••••••" />
              </div>

              {error && <p className="rounded-lg border border-red-800/50 bg-red-950/40 px-4 py-3 text-sm text-red-300" data-testid="auth-error">{error}</p>}

              <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-60" data-testid="auth-submit-btn">
                {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
              </button>
            </form>

            <p className="mt-6 text-center text-xs leading-relaxed text-slate-500">
              By continuing you agree to our terms of service. Payments are processed securely in GBP by Stripe.
            </p>
            <p className="mt-4 text-center text-xs text-slate-500">
              <Link to="/" className="text-[#C5A059] hover:underline" data-testid="auth-back-home">← Back to homepage</Link>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
